import React, { PureComponent } from "react";
import styled, { keyframes } from "styled-components";
import { Loader } from "react-loaders";
import Fragment from "react-dot-fragment";

import { postRequest, putRequest } from "../../../api/ApiService";
import { fetchTeamDecisions } from "../../../api/TeamDecisionsService";
import EventEmitter from "../../../services/event-emitter";
import Styled from "components/finance/style";

// Subtle on-card animations per investor type. Each runs forever so
// the card has a small bit of life even when the user is just hovering
// the picker. Durations are slow (3-4s) so the motion never competes
// with the form below.
const floatY = keyframes`
  0%, 100% { transform: translateY(0); }
  50%     { transform: translateY(-3px); }
`;

const haloGlow = keyframes`
  0%, 100% { opacity: 0.55; transform: scale(1); }
  50%     { opacity: 0.95; transform: scale(1.08); }
`;

const liftRocket = keyframes`
  0%, 100% { transform: translateY(0); }
  50%     { transform: translateY(-4px); }
`;

const flameFlicker = keyframes`
  0%, 100% { transform: scaleY(1)   translateY(0); opacity: 0.9; }
  50%     { transform: scaleY(1.25) translateY(2px); opacity: 1; }
`;

const vaultPulse = keyframes`
  0%, 100% { transform: scale(1); }
  50%     { transform: scale(1.03); }
`;

const coinSpin = keyframes`
  0%   { transform: rotateY(0deg); }
  100% { transform: rotateY(360deg); }
`;

// SVG canvas wrapper — keeps the 84×84 card slot from changing size as
// the children animate. overflow: hidden so a stray translateY doesn't
// bleed past the rounded border on the EquityLogo box.
const InvestorArtFrame = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: inherit;
`;

const AngelFloat = styled.g`
  transform-origin: center;
  animation: ${floatY} 3.2s ease-in-out infinite;
`;

const AngelHalo = styled.ellipse`
  transform-origin: center;
  transform-box: fill-box;
  animation: ${haloGlow} 2.6s ease-in-out infinite;
`;

const RocketLift = styled.g`
  transform-origin: center;
  animation: ${liftRocket} 2.2s ease-in-out infinite;
`;

const RocketFlame = styled.g`
  transform-origin: center top;
  transform-box: fill-box;
  animation: ${flameFlicker} 0.6s ease-in-out infinite;
`;

const VaultPulse = styled.g`
  transform-origin: center;
  animation: ${vaultPulse} 3.6s ease-in-out infinite;
`;

const VaultCoin = styled.g`
  transform-origin: center;
  transform-box: fill-box;
  animation: ${coinSpin} 3.4s linear infinite;
`;

function AngelArt() {
  return (
    <svg viewBox="0 0 64 64" width="60" height="60" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="angelHalo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FFE9A8" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#FFE9A8" stopOpacity="0" />
        </radialGradient>
      </defs>
      <AngelHalo cx="32" cy="14" rx="14" ry="5" fill="url(#angelHalo)" />
      <AngelFloat>
        {/* Wings */}
        <path d="M16 32 C 10 28, 8 36, 14 40 C 18 42, 22 40, 22 36 Z" fill="#EFE7FE" stroke="#A98DF6" strokeWidth="1.2" />
        <path d="M48 32 C 54 28, 56 36, 50 40 C 46 42, 42 40, 42 36 Z" fill="#EFE7FE" stroke="#A98DF6" strokeWidth="1.2" />
        {/* Halo ring */}
        <ellipse cx="32" cy="14" rx="9" ry="2.5" fill="none" stroke="#F2B900" strokeWidth="1.5" />
        {/* Head */}
        <circle cx="32" cy="22" r="6.5" fill="#FFD8B3" stroke="#A98DF6" strokeWidth="1.2" />
        {/* Smile */}
        <path d="M29 23.5 Q 32 26 35 23.5" fill="none" stroke="#5C3D8A" strokeWidth="1.1" strokeLinecap="round" />
        {/* Body */}
        <path d="M22 48 C 22 36, 42 36, 42 48 L 40 52 L 24 52 Z" fill="#FFFFFF" stroke="#A98DF6" strokeWidth="1.4" />
      </AngelFloat>
    </svg>
  );
}

function RocketArt() {
  return (
    <svg viewBox="0 0 64 64" width="60" height="60" xmlns="http://www.w3.org/2000/svg">
      <RocketLift>
        {/* Flame (under the rocket, animated separately) */}
        <RocketFlame>
          <path d="M26 46 Q 32 60 38 46 Q 34 52 32 52 Q 30 52 26 46 Z" fill="#FFB347" />
          <path d="M28 46 Q 32 56 36 46 Q 34 50 32 50 Q 30 50 28 46 Z" fill="#FFE066" />
        </RocketFlame>
        {/* Fins */}
        <path d="M18 38 L 24 32 L 24 46 Z" fill="#7B5BE5" />
        <path d="M46 38 L 40 32 L 40 46 Z" fill="#7B5BE5" />
        {/* Body */}
        <rect x="24" y="20" width="16" height="28" rx="8" fill="#FFFFFF" stroke="#A98DF6" strokeWidth="1.4" />
        {/* Nose cone */}
        <path d="M24 20 Q 32 4 40 20 Z" fill="#A98DF6" />
        {/* Window */}
        <circle cx="32" cy="30" r="4" fill="#EFE7FE" stroke="#7B5BE5" strokeWidth="1.2" />
        <circle cx="32" cy="30" r="2" fill="#7B5BE5" />
      </RocketLift>
    </svg>
  );
}

function VaultArt() {
  return (
    <svg viewBox="0 0 64 64" width="60" height="60" xmlns="http://www.w3.org/2000/svg">
      <VaultPulse>
        {/* Roof pediment */}
        <path d="M10 22 L 32 8 L 54 22 Z" fill="#7B5BE5" />
        <rect x="10" y="22" width="44" height="4" fill="#A98DF6" />
        {/* Columns */}
        <rect x="14" y="26" width="5" height="22" fill="#FFFFFF" stroke="#A98DF6" strokeWidth="1.2" />
        <rect x="22" y="26" width="5" height="22" fill="#FFFFFF" stroke="#A98DF6" strokeWidth="1.2" />
        <rect x="30" y="26" width="5" height="22" fill="#FFFFFF" stroke="#A98DF6" strokeWidth="1.2" />
        <rect x="38" y="26" width="5" height="22" fill="#FFFFFF" stroke="#A98DF6" strokeWidth="1.2" />
        <rect x="46" y="26" width="5" height="22" fill="#FFFFFF" stroke="#A98DF6" strokeWidth="1.2" />
        {/* Base */}
        <rect x="8" y="48" width="48" height="6" rx="1" fill="#7B5BE5" />
      </VaultPulse>
      {/* Spinning coin in front */}
      <VaultCoin>
        <circle cx="32" cy="44" r="6" fill="#F2B900" stroke="#7B5BE5" strokeWidth="1" />
        <text x="32" y="47.5" fontSize="7" fontFamily="Mukta Vaani, Lato, sans-serif"
          fontWeight="700" textAnchor="middle" fill="#7B5BE5">$</text>
      </VaultCoin>
    </svg>
  );
}

function renderInvestorArt(iconKey) {
  switch (iconKey) {
    case "angel":   return <AngelArt />;
    case "rocket":  return <RocketArt />;
    case "vault":   return <VaultArt />;
    default:        return null;
  }
}

// Static per-investor presentation. Keyed by equity_client name
// (case-insensitive substring match). Falls back to index-based shade
// + generic stage label for unknown investors.
const INVESTOR_PRESETS = [
  {
    match: /angel/i,
    shade: "light",
    iconKey: "angel",
    tagline: "Believers with capital",
    stage: "EARLY STAGE",
    description:
      "Wealthy individuals investing personal money. Founder-friendly, patient, and often your first cheerleader, but limited check size."
  },
  {
    match: /venture/i,
    shade: "mid",
    iconKey: "rocket",
    tagline: "Rocket fuel for hypergrowth",
    stage: "GROWTH STAGE",
    description:
      "Professional fund managers chasing 10x+ returns. Aggressive growth expectations, board seats, and access to a powerful network and follow-on capital."
  },
  {
    match: /private equity|p\.?e\.?$/i,
    shade: "dark",
    iconKey: "vault",
    tagline: "We buy companies, not stories",
    stage: "MATURE STAGE",
    description:
      "Institutional capital seeking proven cash flow. Massive checks but expects majority control, operational discipline, and a clear exit within 3 to 5 years."
  }
];

const THEME_BY_INDEX = ["light", "mid", "dark"];

function pickPreset(name, index) {
  const byName = INVESTOR_PRESETS.find(p => p.match.test(name || ""));
  if (byName) return byName;
  const fallbackIcons = ["angel", "rocket", "vault"];
  return {
    shade: THEME_BY_INDEX[index] || "light",
    iconKey: fallbackIcons[index] || null,
    tagline: "",
    stage: index === 0 ? "EARLY STAGE" : index === 1 ? "GROWTH STAGE" : "MATURE STAGE",
    description: ""
  };
}

const formatFullDollar = val => {
  const n = Math.round(Number(val) || 0);
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toLocaleString("en-US")}`;
};

const formatNumber = val => {
  const n = Math.round(Number(val) || 0);
  return n.toLocaleString("en-US");
};

const numberToM = val => {
  const n = Number(val) || 0;
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  return `${sign}$${(abs / 1000000).toFixed(abs >= 10000000 ? 2 : 3)}M`;
};

const formatSharePrice = val => `$${(Number(val) || 0).toFixed(2)}`;
const formatPlainInt = val => Math.round(Number(val) || 0).toLocaleString('en-US');

const LockedCardWrapper = styled.div`
  position: relative;
  cursor: not-allowed;

  /* Dim the underlying card and block interactions with its children. */
  > * {
    opacity: 0.45;
    filter: grayscale(0.65);
    pointer-events: none;
  }
`;

const LockedTooltip = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 10px 18px;
  border-radius: 999px;
  background: rgba(0, 27, 59, 0.94);
  color: #FFFFFF;
  font-family: 'Mukta Vaani';
  font-weight: 600;
  font-size: 16px;
  line-height: 1.4;
  white-space: nowrap;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.28);
  opacity: 0;
  pointer-events: none;
  transition: opacity 120ms ease;
  z-index: 3;

  ${LockedCardWrapper}:hover & {
    opacity: 1;
  }
`;

// Wrapper + tooltip for Buyback / Pay Dividends tabs that are locked
// in Year 1. Same pattern used elsewhere: disabled child kills its own
// pointer-events so the wrapper's :hover reliably fires and the anchor
// sits on the container instead of the disabled button.
const Year1LockTooltipWrapper = styled.span`
  position: relative;
  display: inline-flex;
  flex: 1;

  &:hover > span.year1-lock-tooltip {
    opacity: 1;
    visibility: visible;
  }
`;

const Year1LockTooltip = styled.span`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(8px);
  background: rgba(0, 27, 59, 0.94);
  color: #fff;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  padding: 10px 14px;
  border-radius: 6px;
  width: 280px;
  text-align: center;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease, visibility 0.15s ease;
  pointer-events: none;
  z-index: 10;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-bottom-color: rgba(0, 27, 59, 0.94);
  }
`;

// Animates between numeric values whenever `value` changes (rAF + ease-out
// cubic, 600ms). Mirrors the CountUp used on Choose Your Lender. Renders
// inside a <span> because React 15 requires component render to return an
// element, not a bare string.
class CountUp extends PureComponent {
  constructor(props) {
    super(props);
    this.state = { display: Number(props.value) || 0 };
  }

  componentDidUpdate(prevProps) {
    const next = Number(this.props.value) || 0;
    const prev = Number(prevProps.value) || 0;
    if (next !== prev) {
      this.animateTo(this.state.display, next);
    }
  }

  componentWillUnmount() {
    this._cancelled = true;
  }

  animateTo(from, to) {
    if (this._raf) cancelAnimationFrame(this._raf);
    const duration = 600;
    const start = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const step = () => {
      if (this._cancelled) return;
      const elapsed = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - start;
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const value = from + (to - from) * eased;
      this.setState({ display: t === 1 ? to : value });
      if (t < 1) {
        this._raf = requestAnimationFrame(step);
      }
    };
    this._raf = requestAnimationFrame(step);
  }

  render() {
    const { format } = this.props;
    return <span>{format(this.state.display)}</span>;
  }
}

class EquityDecisionsPage extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      investors: [],
      companyValuation: 0,
      netCapital: 0,
      sharePrice: 0,
      outstandingShares: 0,
      maxShareIssuance: 0,
      cashBalance: 0,
      selectedInvestorId: null,
      // Decision panel state (after an investor is selected)
      activeDecisionTab: 'issue',     // 'issue' | 'buyback' | 'dividends'
      sharesToIssue: '',
      sharesToBuyback: '',
      dividendPerShare: '',
      postingIssue: false,
      postingBuyback: false,
      postingDividends: false,
      noticeOpen: false,
      noticeTitle: '',
      noticeMessage: ''
    };
    this.eventemitter = EventEmitter.getInstance();
  }

  showPopupTooManyShares = (sharePriceLabel) => {
    this.eventemitter.trigger("coaching-feedback", [
      "Issuing Ownership",
      <Fragment>
        Make it rain! Raise that equity! But don't forget, you are issuing a lot of shares at a very low price of {sharePriceLabel}.
        <br /><br />
        Pop quiz: If you expect your business value to increase over time (i.e. expect your share price to go up), would you rather issue so many shares at a lower price today or wait to issue at a higher price tomorrow?
      </Fragment>,
      <Fragment>
        To learn more about your capital raising limits, please read The Investor section of the Business Case.
      </Fragment>,
    ]);
  };

  showPopupTooFewShares = (sharePriceLabel, capitalRaised) => {
    const capitalFormatted = Math.round(Number(capitalRaised) || 0).toLocaleString("en-US");
    this.eventemitter.trigger("coaching-feedback", [
      "Issuing Ownership",
      <Fragment>
        Very Interesting. You're only raising ${capitalFormatted} of equity capital! I get it, you don't want to issue ownership shares at this very low price of {sharePriceLabel}, but is that enough equity to raise? You could try to raise your remaining capital needs through debt, but your borrowing limits is based on your equity raised (i.e. the more equity raised, the more money lenders will lend).
      </Fragment>,
      <Fragment>
        To learn more about your capital raising limits, please read The Investor section of the Business Case.
      </Fragment>,
    ]);
  };

  componentDidMount() {
    this._isMounted = true;
    this.fetchAll();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  // Shape the consolidated team-decisions response into the state we
  // render against. Single source of truth for both initial load and
  // post-action refresh.
  buildStateFromDecisions = (decisions) => {
    const { roundId, roundNumber, isActiveRound } = this.props;
    const equityClients = decisions.equity_clients || [];
    const equities = decisions.equities || [];
    const repurchasedSharesArr = decisions.repurchased_shares || decisions.stock_repurchased || [];
    const dividendsPaymentArr =
      decisions.dividends_payments || decisions.dividends_payment || decisions.dividend_payments || [];
    const preMoneysArr = decisions.pre_moneys || decisions.pre_money || [];
    const postMoneysArr = decisions.post_moneys || [];
    const performanceMoneysArr = decisions.performance_moneys || [];
    const constantsObj = decisions.constants || {};
    const financial = (decisions.financial_decisions && decisions.financial_decisions.team) || {};

    // Always filter snapshot arrays by the round we're viewing. The
    // team-decisions endpoint can return historical rows alongside the
    // current-round row, so picking by array index gives the wrong
    // round. Within a round we take the last matching entry to honor
    // the "latest snapshot" semantic when multiple rows exist.
    const lastFor = (arr, id) => {
      const matches = arr.filter(r => Number(r.round) === Number(id));
      return matches.length > 0 ? matches[matches.length - 1] : null;
    };
    const postMoneyRow = lastFor(postMoneysArr, roundId);
    const perfMoneyRow = lastFor(performanceMoneysArr, roundId);

    // The most recent performance_moneys row for any round prior to the
    // current one (highest round number < roundId, last entry within
    // that round). Used as the active-round fallback when post_moneys
    // hasn't been written yet.
    const priorPerfRows = performanceMoneysArr
      .filter(r => Number(r.round) < Number(roundId));
    const maxPriorRoundId = priorPerfRows.reduce(
      (m, r) => Math.max(m, Number(r.round)), -Infinity);
    const priorPerfRow = priorPerfRows.length > 0
      ? lastFor(priorPerfRows, maxPriorRoundId)
      : null;

    // Resolve the status-bar trio:
    //   • Closed round  → performance_moneys for this round
    //   • Active round, post_moneys present → post_moneys
    //   • Active round, no post_moneys, round > 1 → prior round's performance_moneys
    //   • Active round, no post_moneys, round 1   → constants (initial_*)
    let companyValuation = 0;
    let sharePrice = 0;
    let outstandingShares = 0;

    if (!isActiveRound) {
      if (perfMoneyRow) {
        companyValuation = Number(perfMoneyRow.equity_value) || 0;
        sharePrice = Number(perfMoneyRow.share_price) || 0;
        outstandingShares = Number(perfMoneyRow.shares) || 0;
      }
    } else if (postMoneyRow) {
      companyValuation = Number(postMoneyRow.valuation) || 0;
      sharePrice = Number(postMoneyRow.share_price) || 0;
      outstandingShares = Number(postMoneyRow.shares) || 0;
    } else if (Number(roundNumber) > 1 && priorPerfRow) {
      companyValuation = Number(priorPerfRow.equity_value) || 0;
      sharePrice = Number(priorPerfRow.share_price) || 0;
      outstandingShares = Number(priorPerfRow.shares) || 0;
    } else {
      // Active round 1 (or no prior data at all): seed from game constants.
      companyValuation = Number(constantsObj.initial_valuation) || 0;
      sharePrice = Number(constantsObj.initial_share_price) || 0;
      outstandingShares = Number(constantsObj.initial_shares) || 0;
    }

    // Available Shares to Issue cap:
    //   1. current round post_moneys.max_share_issuance if present
    //   2. else prior round pre_moneys.max_share_issuance (highest round
    //      number < current roundId)
    let maxShareIssuance = 0;
    if (postMoneyRow && postMoneyRow.max_share_issuance != null) {
      maxShareIssuance = Number(postMoneyRow.max_share_issuance) || 0;
    } else {
      const priorPreRows = preMoneysArr.filter(r => Number(r.round) < Number(roundId));
      if (priorPreRows.length > 0) {
        const maxPriorPreRoundId = priorPreRows.reduce(
          (m, r) => Math.max(m, Number(r.round)), -Infinity);
        const priorPreRow = lastFor(priorPreRows, maxPriorPreRoundId);
        maxShareIssuance = priorPreRow
          ? Number(priorPreRow.max_share_issuance) || 0
          : 0;
      }
    }

    const cashBalance = Number(financial.ending_cash) || 0;

    const investors = equityClients.map((client) => {
      const clientEquities = equities.filter(e => e.client === client.id);
      const clientRepurchases = repurchasedSharesArr.filter(r => r.client === client.id);
      const issuedForClient = clientEquities
        .reduce((sum, e) => sum + (Number(e.issuance) || 0), 0);
      const capitalInvestedGross = clientEquities
        .reduce((sum, e) => sum + (Number(e.capital) || 0), 0);
      const repurchasedForClient = clientRepurchases
        .reduce((sum, r) => sum + (Number(r.shares) || 0), 0);
      // Net Capital Invested = Σ equities.capital − Σ repurchased_shares.cost
      // (across all rounds for this client).
      const repurchasedCostForClient = clientRepurchases
        .reduce((sum, r) => sum + (Number(r.cost) || 0), 0);
      const capitalInvested = capitalInvestedGross - repurchasedCostForClient;

      const capitalLimit = Number(client.capital_limit) || 0;
      const thisRoundEquity = clientEquities.find(
        e => Number(e.round) === Number(roundId));
      const thisRoundRepurchase = repurchasedSharesArr.find(
        r => r.client === client.id && Number(r.round) === Number(roundId));
      const thisRoundDividend = dividendsPaymentArr.find(
        d => d.client === client.id && Number(d.round) === Number(roundId));

      const equityIssuanceThisRound = thisRoundEquity ? Number(thisRoundEquity.issuance) || 0 : 0;
      const equityCapitalThisRound = thisRoundEquity ? Number(thisRoundEquity.capital) || 0 : 0;
      const repurchaseSharesThisRound = thisRoundRepurchase ? Number(thisRoundRepurchase.shares) || 0 : 0;
      const repurchaseCostThisRound = thisRoundRepurchase ? Number(thisRoundRepurchase.cost) || 0 : 0;
      const dividendPaymentThisRound = thisRoundDividend ? Number(thisRoundDividend.payment) || 0 : 0;

      // Per-round sums used by the card's "Capital Invested/Returned"
      // block. Sign drives the verb (Invested vs Returned); the displayed
      // value is the absolute magnitude.
      //   shares this round   = equities.issuance − repurchased_shares.shares
      //   capital this round  = equities.capital  − repurchased_shares.cost
      //                                            − dividends_payments.payment
      const sharesValueThisRound = equityIssuanceThisRound - repurchaseSharesThisRound;
      const capitalValueThisRound =
        equityCapitalThisRound - repurchaseCostThisRound - dividendPaymentThisRound;

      return {
        client_id: client.id,
        name: client.name,
        capitalLimit,
        maxAmount: Math.max(0, capitalLimit - capitalInvested),
        sharesOwned: issuedForClient - repurchasedForClient,
        capitalInvested,
        sharesValueThisRound,
        capitalValueThisRound,
        equityIdThisRound: thisRoundEquity ? thisRoundEquity.id : null,
        equityIssuanceThisRound,
        equityCapitalThisRound,
        repurchaseIdThisRound: thisRoundRepurchase ? thisRoundRepurchase.id : null,
        repurchaseSharesThisRound,
        repurchaseCostThisRound,
        dividendIdThisRound: thisRoundDividend ? thisRoundDividend.id : null,
        dividendPaymentThisRound
      };
    });

    // Net Capital Raised/Returned for equity (this round) =
    //   Σ equities.capital - (Σ repurchased_shares.cost + Σ dividends_payments.payment).
    // Positive = net inflow this round; negative = net outflow (e.g.
    // dividends paid out). Filter every array to the active round so
    // the header reflects current-round activity only.
    const capitalRaised = equities
      .filter(e => Number(e.round) === Number(roundId))
      .reduce((sum, e) => sum + (Number(e.capital) || 0), 0);
    const repurchaseCost = repurchasedSharesArr
      .filter(r => Number(r.round) === Number(roundId))
      .reduce((sum, r) => sum + (Number(r.cost) || 0), 0);
    const dividendsPaid = dividendsPaymentArr
      .filter(d => Number(d.round) === Number(roundId))
      .reduce((sum, d) => sum + (Number(d.payment) || 0), 0);
    const netCapital = capitalRaised - (repurchaseCost + dividendsPaid);

    return {
      investors,
      companyValuation,
      netCapital,
      sharePrice,
      outstandingShares,
      maxShareIssuance,
      cashBalance
    };
  };

  // Single team-decisions fetch, with one conditional follow-up call
  // for round (N-1) when we're on an active round that doesn't yet
  // have post_moneys data. Returns { decisions, priorDecisions }.
  loadData = () => {
    const { roundNumber, isActiveRound } = this.props;
    return fetchTeamDecisions(roundNumber, "finance").then((decisions) => {
      const needsPrior =
        isActiveRound
        && (decisions.post_moneys || []).length === 0
        && Number(roundNumber) > 1;
      if (!needsPrior) return { decisions, priorDecisions: null };
      return fetchTeamDecisions(Number(roundNumber) - 1, "finance")
        .then((priorDecisions) => ({ decisions, priorDecisions }))
        .catch(() => ({ decisions, priorDecisions: null }));
    });
  };

  fetchAll = () => {
    this.loadData()
      .then(({ decisions, priorDecisions }) => {
        if (!this._isMounted) return;
        this.setState({
          loading: false,
          ...this.buildStateFromDecisions(decisions, priorDecisions)
        });
      })
      .catch(() => {
        if (!this._isMounted) return;
        this.setState({ loading: false });
      });
  };

  handleSelectInvestorInternal = (investor) => {
    // Reset all input state so the next investor starts fresh; render()
    // shows the existing per-round value as a ghost placeholder.
    this.setState({
      selectedInvestorId: investor.client_id,
      activeDecisionTab: 'issue',
      sharesToIssue: '',
      sharesToBuyback: '',
      dividendPerShare: ''
    });
  };

  handleChooseDifferentInvestor = () => {
    this.setState({
      selectedInvestorId: null,
      sharesToIssue: '',
      sharesToBuyback: '',
      dividendPerShare: ''
    });
  };

  setActiveDecisionTab = (tab) => {
    this.setState({ activeDecisionTab: tab });
  };

  handleSharesToIssueChange = (e) => {
    const val = e.target.value.replace(/[^\d]/g, '');
    this.setState({ sharesToIssue: val });
  };

  handleSharesToBuybackChange = (e) => {
    const val = e.target.value.replace(/[^\d]/g, '');
    this.setState({ sharesToBuyback: val });
  };

  handleDividendPerShareChange = (e) => {
    // Cents-as-you-type: every keystroke shifts the decimal. The state
    // holds the raw digit string (no decimal); the displayed value is
    // digits / 100 with .toFixed(2). e.g. "1" → "0.01", "135" → "1.35",
    // "200" → "2.00". Strip non-digits and any leading zeros so the
    // numeric value stays accurate without padding indefinitely.
    const digits = e.target.value.replace(/[^\d]/g, '').replace(/^0+(?=\d)/, '');
    this.setState({ dividendPerShare: digits });
  };

  showNotice = (title, message) => {
    this.setState({ noticeOpen: true, noticeTitle: title, noticeMessage: message });
  };

  // Pull an error message out of an HTTP error. Looks at common shapes:
  //   - body.detail            (DRF default)
  //   - body.non_field_errors  (DRF validators)
  //   - any field-level error  (e.g. { amount: ["..."] })
  //   - plain string body
  // Returns null when nothing usable is found.
  extractBackendError = (err) => {
    const body = err && err.response && err.response.body;
    if (!body) return null;
    if (typeof body === 'string') return body;
    if (body.detail) return String(body.detail);
    if (Array.isArray(body.non_field_errors) && body.non_field_errors.length > 0) {
      return String(body.non_field_errors[0]);
    }
    if (body.message) return String(body.message);
    if (body.error) return String(body.error);
    // Field-level: { fieldname: ["error msg"] } or { fieldname: "error msg" }
    for (const key of Object.keys(body)) {
      const val = body[key];
      if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'string') {
        return `${key}: ${val[0]}`;
      }
      if (typeof val === 'string' && val.length > 0) {
        return `${key}: ${val}`;
      }
    }
    return null;
  };

  hideNotice = () => {
    this.setState({ noticeOpen: false });
  };

  refreshAfterPost = () => {
    // Re-fetch team-decisions (and the prior-round fallback if needed)
    // so counters and UPDATE buttons pick up the new rows.
    // Also notify the parent so the Finance Decisions page can
    // re-fetch its KPIs (debt_to_capital, outstanding debt, etc.).
    if (typeof this.props.onDecisionMade === 'function') {
      this.props.onDecisionMade();
    }
    return new Promise((resolve) => {
      this.loadData()
        .then(({ decisions, priorDecisions }) => {
          if (!this._isMounted) { resolve(); return; }
          this.setState(this.buildStateFromDecisions(decisions, priorDecisions), resolve);
        })
        .catch(() => resolve());
    });
  };

  handlePostIssueShares = () => {
    const { investors, selectedInvestorId, sharesToIssue, sharePrice, maxShareIssuance } = this.state;
    const { roundId } = this.props;
    const investor = investors.find(i => i.client_id === selectedInvestorId);
    if (!investor) return;

    const shares = Number(sharesToIssue) || 0;
    if (shares < 0) {
      this.showNotice('Invalid amount', 'Share amount cannot be negative.');
      return;
    }

    // Only validate caps when the user is INCREASING this round's
    // issuance. If they're modifying down (or leaving it the same), the
    // current-round Available cap isn't relevant — the existing record
    // was already accepted, and a smaller value strictly reduces exposure.
    const existingIssuance = Number(investor.equityIssuanceThisRound) || 0;
    const isIncreasing = shares > existingIssuance;

    // Available Shares to Issue = min(post_moneys.max_share_issuance for
    // this round (or prior round's pre_moneys fallback), capital_limit / share_price)
    const capLimitShares = sharePrice > 0
      ? Math.floor(Number(investor.capitalLimit || 0) / sharePrice)
      : 0;
    // Use maxShareIssuance directly (not `|| Infinity`) — a value of 0
    // means "cap exhausted this round" and must be honored, not treated
    // as missing.
    const availableToIssue = Math.min(maxShareIssuance, capLimitShares);
    // availableToIssue is REMAINING capacity net of shares already
    // booked this round. Compare the incremental delta against it, not
    // the new total — otherwise editing an existing issuance from
    // 5.5M up to 6M reads as "6M > 3.5M remaining" even though the
    // +0.5M delta fits. Same shape as the capital check below.
    const incrementalShares = shares - existingIssuance;
    if (isIncreasing && incrementalShares > availableToIssue) {
      const totalAllowed = existingIssuance + availableToIssue;
      this.showNotice(
        'Exceeds Available Shares to Issue',
        `You can issue up to ${totalAllowed.toLocaleString('en-US')} shares to ${investor.name} this round.`
      );
      return;
    }

    // maxAmount is REMAINING capacity beyond capital already booked
    // (including this round's existing issuance). So compare the
    // incremental delta the user is adding against maxAmount, not the
    // new total against maxAmount — otherwise editing an existing
    // raise from $3.75M up to $6M reads as "$6M > $2.25M remaining"
    // even though the incremental $2.25M fits exactly.
    const alreadyRaisedThisRound = Number(investor.equityCapitalThisRound) || 0;
    const expectedCapital = shares * sharePrice;
    const incrementalCapital = expectedCapital - alreadyRaisedThisRound;
    if (isIncreasing && incrementalCapital > investor.maxAmount) {
      const totalAllowed = alreadyRaisedThisRound + investor.maxAmount;
      this.showNotice(
        'Exceeds Max Amount',
        `Expected capital raised of $${Math.round(expectedCapital).toLocaleString('en-US')} exceeds what ${investor.name} can offer. You can raise up to $${Math.round(totalAllowed).toLocaleString('en-US')} in total this round.`
      );
      return;
    }

    // Capture BEFORE the POST: is this Round 1 AND the team's first
    // equity issuance ever? Fires the coaching popup exactly once,
    // on the very first raise.
    const wasFirstEquityRd1 =
      Number(this.props.roundNumber) === 1
      && !investors.some(i => Number(i.equityIssuanceThisRound) > 0);

    this.setState({ postingIssue: true });
    const apiUrl = process.env.REACT_APP_API_URL;
    const body = { client: investor.client_id, issuance: shares, round: roundId };
    const req = investor.equityIdThisRound
      ? putRequest(`${apiUrl}team/equities/${investor.equityIdThisRound}/`)
      : postRequest(`${apiUrl}team/equities/`);

    req.send(body)
      .then(res => {
        // Coaching popups (ported from raise-equity-modal): issuing
        // > 4.5M shares warns about too many at low price; issuing
        // <= 1M shares warns about too little equity for downstream debt.
        const respBody = (res && res.body) || {};
        const issuance = Number(respBody.issuance) || 0;
        const capital = Number(respBody.capital) || 0;
        const sharePriceLabel = issuance > 0
          ? `$${(capital / issuance).toFixed(2)}`
          : "$0.00";
        if (issuance > 4500000) {
          this.showPopupTooManyShares(sharePriceLabel);
        } else if (issuance > 0 && issuance <= 1000000) {
          this.showPopupTooFewShares(sharePriceLabel, capital);
        }
        if (wasFirstEquityRd1 && this.props.onFirstEquityIssued) {
          this.props.onFirstEquityIssued();
        }
        return this.refreshAfterPost();
      })
      .then(() => {
        if (!this._isMounted) return;
        this.setState({ postingIssue: false, sharesToIssue: '' });
      })
      .catch(err => {
        if (!this._isMounted) return;
        const msg = this.extractBackendError(err) || 'Failed to post share issuance.';
        this.setState({ postingIssue: false });
        this.showNotice('Post Failed', msg);
      });
  };

  handlePostBuyback = () => {
    const { investors, selectedInvestorId, sharesToBuyback, sharePrice, cashBalance } = this.state;
    const { roundId } = this.props;
    const investor = investors.find(i => i.client_id === selectedInvestorId);
    if (!investor) return;

    const shares = Number(sharesToBuyback) || 0;
    if (shares < 0) {
      this.showNotice('Invalid amount', 'Share amount cannot be negative.');
      return;
    }

    // Cannot buyback more than this investor currently owns
    // (issued − previously repurchased from this investor). sharesOwned
    // already subtracts THIS round's buyback, so add it back to get
    // the "true" cap and gate on isIncreasing — otherwise editing a
    // 500-share buyback down to 300 reads as "300 > 0" and false-fires.
    const existingBuyback = Number(investor.repurchaseSharesThisRound) || 0;
    const isIncreasing = shares > existingBuyback;
    const effectiveAvailable = (Number(investor.sharesOwned) || 0) + existingBuyback;
    if (isIncreasing && shares > effectiveAvailable) {
      this.showNotice(
        'Exceeds Available Shares to Buyback',
        `${investor.name} currently owns ${effectiveAvailable.toLocaleString('en-US')} shares. You can repurchase up to that amount.`
      );
      return;
    }

    // Cash check — only the incremental cost hits cash. The already-
    // booked buyback cost was deducted when it was posted, so editing
    // up by 100 shares only needs (100 × sharePrice) in available cash.
    const newTotalCost = shares * (Number(sharePrice) || 0);
    const existingBuybackCost = Number(investor.repurchaseCostThisRound) || 0;
    const additionalCashNeeded = Math.max(0, newTotalCost - existingBuybackCost);
    if (isIncreasing && additionalCashNeeded > Number(cashBalance || 0)) {
      this.showNotice('Insufficient Cash',
        `Your available cash balance is ${numberToM(cashBalance)}. Reduce the buyback amount or raise additional capital first.`);
      return;
    }

    this.setState({ postingBuyback: true });
    const apiUrl = process.env.REACT_APP_API_URL;
    const body = { client: investor.client_id, round: roundId, shares };
    const req = investor.repurchaseIdThisRound
      ? putRequest(`${apiUrl}team/stock_repurchased/${investor.repurchaseIdThisRound}/`)
      : postRequest(`${apiUrl}team/stock_repurchased/`);

    req.send(body)
      .then(() => this.refreshAfterPost())
      .then(() => {
        if (!this._isMounted) return;
        this.setState({ postingBuyback: false, sharesToBuyback: '' });
      })
      .catch(err => {
        if (!this._isMounted) return;
        const msg = this.extractBackendError(err) || 'Failed to post share buyback.';
        this.setState({ postingBuyback: false });
        this.showNotice('Post Failed', msg);
      });
  };

  handlePostDividends = () => {
    const { investors, selectedInvestorId, dividendPerShare, cashBalance } = this.state;
    const { roundId } = this.props;
    const investor = investors.find(i => i.client_id === selectedInvestorId);
    if (!investor) return;

    // State stores raw digits (cents-as-you-type); value = digits / 100.
    const dps = (Number(dividendPerShare) || 0) / 100;
    // Allow $0 only when updating an existing dividend record (PUT) so
    // students can cancel a dividend they already paid this round.
    // Posting a fresh $0 dividend has no effect and is still blocked so
    // we don't create noise records.
    const isUpdate = !!investor.dividendIdThisRound;
    if (dps < 0 || (dps === 0 && !isUpdate)) {
      this.showNotice('Invalid amount', 'Dividend per share must be greater than zero.');
      return;
    }

    // Backend `payment` field expects the Total Payout (dollars paid out),
    // not the per-share rate. Total Payout = dps × this investor's net
    // shares owned.
    const totalPayout = dps * (Number(investor.sharesOwned) || 0);
    // Cash check — same delta-vs-total rationale as buyback: the prior
    // dividend payment was already deducted from cashBalance, so only
    // the incremental payout hits cash on a PUT.
    const existingPayment = Number(investor.dividendPaymentThisRound) || 0;
    const isIncreasing = totalPayout > existingPayment;
    const additionalCashNeeded = Math.max(0, totalPayout - existingPayment);
    if (isIncreasing && additionalCashNeeded > Number(cashBalance || 0)) {
      this.showNotice('Insufficient Cash',
        `Your available cash balance is ${numberToM(cashBalance)}. Reduce the dividend per share or raise additional capital first.`);
      return;
    }

    this.setState({ postingDividends: true });
    const apiUrl = process.env.REACT_APP_API_URL;
    const body = {
      client: investor.client_id,
      round: roundId,
      payment: totalPayout
    };
    const req = investor.dividendIdThisRound
      ? putRequest(`${apiUrl}team/dividends_payment/${investor.dividendIdThisRound}/`)
      : postRequest(`${apiUrl}team/dividends_payment/`);

    req.send(body)
      .then(() => this.refreshAfterPost())
      .then(() => {
        if (!this._isMounted) return;
        this.setState({ postingDividends: false, dividendPerShare: '' });
      })
      .catch(err => {
        if (!this._isMounted) return;
        const msg = this.extractBackendError(err) || 'Failed to post dividend payment.';
        this.setState({ postingDividends: false });
        this.showNotice('Post Failed', msg);
      });
  };

  renderDecisionPanel = (investor) => {
    const { activeDecisionTab, cashBalance } = this.state;
    const isYear1 = Number(this.props.roundNumber) === 1;
    const isNoCash = Number(cashBalance) < 0;
    // Exception to the no-cash lock: if the team already posted a
    // buyback or dividend for this investor this round, keep that tab
    // reachable so they can reduce or cancel the decision and free up
    // cash. handlePostBuyback / handlePostDividends already gate the
    // insufficient-cash guard on `isIncreasing`, so reducing an
    // existing record can't dig the hole any deeper.
    const hasExistingBuyback = !!investor.repurchaseIdThisRound;
    const hasExistingDividend = !!investor.dividendIdThisRound;
    const buybackLocked = isNoCash && !hasExistingBuyback;
    const dividendsLocked = isNoCash && !hasExistingDividend;
    const lockedTabStyle = { opacity: 0.5, pointerEvents: 'none', width: '100%' };
    return (
      <Styled.DecisionOptionsWrap>
        <Styled.DecisionOptionsTitle>Decision Options</Styled.DecisionOptionsTitle>
        <Styled.DecisionTabBar>
          <Styled.DecisionTab
            active={activeDecisionTab === 'issue'}
            onClick={() => this.setActiveDecisionTab('issue')}
          >Issue Shares</Styled.DecisionTab>
          {isYear1 ? (
            <Year1LockTooltipWrapper style={{ cursor: 'not-allowed' }}>
              <Styled.DecisionTab disabled style={lockedTabStyle}>Buyback Shares</Styled.DecisionTab>
              <Year1LockTooltip className="year1-lock-tooltip">
                Buyback Shares isn't available in Year 1. You'll be able to use it in later years.
              </Year1LockTooltip>
            </Year1LockTooltipWrapper>
          ) : buybackLocked ? (
            <Year1LockTooltipWrapper style={{ cursor: 'not-allowed' }}>
              <Styled.DecisionTab disabled style={lockedTabStyle}>Buyback Shares</Styled.DecisionTab>
              <Year1LockTooltip className="year1-lock-tooltip">
                You have insufficient capital to buyback shares.
              </Year1LockTooltip>
            </Year1LockTooltipWrapper>
          ) : (
            <Styled.DecisionTab
              active={activeDecisionTab === 'buyback'}
              onClick={() => this.setActiveDecisionTab('buyback')}
            >Buyback Shares</Styled.DecisionTab>
          )}
          {isYear1 ? (
            <Year1LockTooltipWrapper style={{ cursor: 'not-allowed' }}>
              <Styled.DecisionTab disabled style={lockedTabStyle}>Pay Dividends</Styled.DecisionTab>
              <Year1LockTooltip className="year1-lock-tooltip">
                Pay Dividends isn't available in Year 1. You'll be able to use it in later years.
              </Year1LockTooltip>
            </Year1LockTooltipWrapper>
          ) : dividendsLocked ? (
            <Year1LockTooltipWrapper style={{ cursor: 'not-allowed' }}>
              <Styled.DecisionTab disabled style={lockedTabStyle}>Pay Dividends</Styled.DecisionTab>
              <Year1LockTooltip className="year1-lock-tooltip">
                You have insufficient funds to pay dividends.
              </Year1LockTooltip>
            </Year1LockTooltipWrapper>
          ) : (
            <Styled.DecisionTab
              active={activeDecisionTab === 'dividends'}
              onClick={() => this.setActiveDecisionTab('dividends')}
            >Pay Dividends</Styled.DecisionTab>
          )}
        </Styled.DecisionTabBar>
        {activeDecisionTab === 'issue' && this.renderIssueSharesPanel(investor)}
        {activeDecisionTab === 'buyback' && !isYear1 && !buybackLocked && this.renderBuybackPanel(investor)}
        {activeDecisionTab === 'dividends' && !isYear1 && !dividendsLocked && this.renderDividendsPanel(investor)}
      </Styled.DecisionOptionsWrap>
    );
  };

  renderIssueSharesPanel = (investor) => {
    const { sharesToIssue, postingIssue, sharePrice, maxShareIssuance } = this.state;

    const existingShares = Math.round(Number(investor.equityIssuanceThisRound) || 0);
    const hasExisting = existingShares > 0;
    const sharesDisplay = sharesToIssue !== ''
      ? Number(sharesToIssue).toLocaleString('en-US')
      : (hasExisting ? existingShares.toLocaleString('en-US') : '');
    const sharesForCalc = sharesToIssue !== ''
      ? Number(sharesToIssue) || 0
      : (hasExisting ? existingShares : 0);
    const capitalRaised = sharesForCalc * sharePrice;
    const changed = sharesToIssue !== '' && Number(sharesToIssue) !== existingShares;
    // Available Shares to Issue = min(pre_moneys.max_share_issuance,
    //                                 equity_clients.capital_limit / share_price)
    const capLimitShares = sharePrice > 0
      ? Math.floor(Number(investor.capitalLimit || 0) / sharePrice)
      : 0;
    // Use maxShareIssuance directly (not `|| Infinity`) — a value of 0
    // means "cap exhausted this round" and must be honored, not treated
    // as missing.
    const availableToIssue = Math.min(maxShareIssuance, capLimitShares);

    return (
      <Styled.DecisionPanel>
        <Styled.DecisionPanelTitle>Issue New Shares to {investor.name}</Styled.DecisionPanelTitle>
        <Styled.DecisionPanelSubtitle>
          Raise capital by issuing new shares from your authorized pool. New shares dilute existing ownership.
        </Styled.DecisionPanelSubtitle>
        <Styled.DecisionInputsRow>
          <Styled.DecisionInputGroup>
            <Styled.DecisionInputLabel>Shares to Issue</Styled.DecisionInputLabel>
            <Styled.DecisionAmountField>
              <Styled.DecisionAmountInput
                type="text"
                inputMode="numeric"
                value={sharesDisplay}
                onChange={this.handleSharesToIssueChange}
                placeholder="0"
                hasButton
              />
              {hasExisting ? (
                <Styled.DecisionUpdateButton
                  onClick={this.handlePostIssueShares}
                  disabled={postingIssue || !changed}
                >
                  {postingIssue ? 'UPDATING...' : 'UPDATE'}
                </Styled.DecisionUpdateButton>
              ) : (
                <Styled.DecisionPostButton
                  onClick={this.handlePostIssueShares}
                  disabled={postingIssue}
                >
                  {postingIssue ? 'ISSUING...' : 'ISSUE'}
                </Styled.DecisionPostButton>
              )}
            </Styled.DecisionAmountField>
          </Styled.DecisionInputGroup>
          <Styled.DecisionInputGroup>
            <Styled.DecisionInputLabel>Capital Raised</Styled.DecisionInputLabel>
            <Styled.DecisionAmountField>
              <Styled.DecisionAmountPrefix>$</Styled.DecisionAmountPrefix>
              <Styled.DecisionAmountStatic>
                <CountUp value={capitalRaised} format={formatPlainInt} />
              </Styled.DecisionAmountStatic>
            </Styled.DecisionAmountField>
          </Styled.DecisionInputGroup>
        </Styled.DecisionInputsRow>
        <Styled.DecisionFooterRow>
          <Styled.DecisionFooterLabel>
            {`Available Shares to Issue in Round ${Number(this.props.roundNumber) || 1}:`}
          </Styled.DecisionFooterLabel>
          <Styled.DecisionFooterValue>
            <CountUp value={availableToIssue} format={formatNumber} />
          </Styled.DecisionFooterValue>
        </Styled.DecisionFooterRow>
      </Styled.DecisionPanel>
    );
  };

  renderBuybackPanel = (investor) => {
    const { sharesToBuyback, postingBuyback, sharePrice } = this.state;

    const existingShares = Math.round(Number(investor.repurchaseSharesThisRound) || 0);
    const existingCost = Number(investor.repurchaseCostThisRound) || 0;
    const hasExisting = existingShares > 0;
    const sharesDisplay = sharesToBuyback !== ''
      ? Number(sharesToBuyback).toLocaleString('en-US')
      : (hasExisting ? existingShares.toLocaleString('en-US') : '');
    const cashOutflow = sharesToBuyback !== ''
      ? Number(sharesToBuyback) * sharePrice
      : (hasExisting ? existingCost : 0);
    const changed = sharesToBuyback !== '' && Number(sharesToBuyback) !== existingShares;

    return (
      <Styled.DecisionPanel>
        <Styled.DecisionPanelTitle>Shares to Repurchase</Styled.DecisionPanelTitle>
        <Styled.DecisionPanelSubtitle>
          Repurchase shares from the investor.
        </Styled.DecisionPanelSubtitle>
        <Styled.DecisionInputsRow>
          <Styled.DecisionInputGroup>
            <Styled.DecisionInputLabel>Shares to Buyback</Styled.DecisionInputLabel>
            <Styled.DecisionAmountField>
              <Styled.DecisionAmountInput
                type="text"
                inputMode="numeric"
                value={sharesDisplay}
                onChange={this.handleSharesToBuybackChange}
                placeholder="0"
                hasButton
              />
              {hasExisting ? (
                <Styled.DecisionUpdateButton
                  onClick={this.handlePostBuyback}
                  disabled={postingBuyback || !changed}
                >
                  {postingBuyback ? 'UPDATING...' : 'UPDATE'}
                </Styled.DecisionUpdateButton>
              ) : (
                <Styled.DecisionPostButton
                  onClick={this.handlePostBuyback}
                  disabled={postingBuyback}
                >
                  {postingBuyback ? 'BUYING BACK...' : 'BUYBACK'}
                </Styled.DecisionPostButton>
              )}
            </Styled.DecisionAmountField>
          </Styled.DecisionInputGroup>
          <Styled.DecisionInputGroup>
            <Styled.DecisionInputLabel>Cash Outflow</Styled.DecisionInputLabel>
            <Styled.DecisionAmountField>
              <Styled.DecisionAmountPrefix>$</Styled.DecisionAmountPrefix>
              <Styled.DecisionAmountStatic>
                <CountUp value={cashOutflow} format={formatPlainInt} />
              </Styled.DecisionAmountStatic>
            </Styled.DecisionAmountField>
          </Styled.DecisionInputGroup>
        </Styled.DecisionInputsRow>
        <Styled.DecisionFooterRow>
          <Styled.DecisionFooterLabel>Available Shares to Buyback:</Styled.DecisionFooterLabel>
          <Styled.DecisionFooterValue>
            <CountUp value={investor.sharesOwned} format={formatNumber} />
          </Styled.DecisionFooterValue>
        </Styled.DecisionFooterRow>
      </Styled.DecisionPanel>
    );
  };

  renderDividendsPanel = (investor) => {
    const { dividendPerShare, postingDividends, cashBalance } = this.state;
    // Cents-as-you-type: state holds raw digits, displayed value is
    // digits/100 with 2 decimal places.
    const sharesOwned = Number(investor.sharesOwned) || 0;
    const existingPayment = Number(investor.dividendPaymentThisRound) || 0;
    // Existing per-share rate, in cents for state-shape comparison.
    const existingDpsCents = sharesOwned > 0
      ? Math.round((existingPayment / sharesOwned) * 100)
      : 0;
    const hasExisting = existingPayment > 0 && sharesOwned > 0;

    const typedCents = Number(dividendPerShare) || 0;
    const cents = dividendPerShare !== '' ? typedCents : (hasExisting ? existingDpsCents : 0);
    const dps = cents / 100;
    const dividendDisplay = dividendPerShare !== ''
      ? (typedCents / 100).toFixed(2)
      : (hasExisting ? (existingDpsCents / 100).toFixed(2) : '');
    const totalPayout = dps * sharesOwned;
    const changed = dividendPerShare !== '' && typedCents !== existingDpsCents;

    return (
      <Styled.DecisionPanel>
        <Styled.DecisionPanelTitle>Dividend Payments</Styled.DecisionPanelTitle>
        <Styled.DecisionPanelSubtitle>
          Pay cash dividends to this investor.
        </Styled.DecisionPanelSubtitle>
        <Styled.DecisionInputsRow>
          <Styled.DecisionInputGroup>
            <Styled.DecisionInputLabel>Dividend Per Share</Styled.DecisionInputLabel>
            <Styled.DecisionAmountField>
              <Styled.DecisionAmountPrefix>$</Styled.DecisionAmountPrefix>
              <Styled.DecisionAmountInput
                type="text"
                inputMode="numeric"
                value={dividendDisplay}
                onChange={this.handleDividendPerShareChange}
                placeholder="0.00"
                hasButton
              />
              {hasExisting ? (
                <Styled.DecisionUpdateButton
                  onClick={this.handlePostDividends}
                  disabled={postingDividends || !changed}
                >
                  {postingDividends ? 'UPDATING...' : 'UPDATE'}
                </Styled.DecisionUpdateButton>
              ) : (
                <Styled.DecisionPostButton
                  onClick={this.handlePostDividends}
                  disabled={postingDividends || dps <= 0}
                >
                  {postingDividends ? 'PAYING...' : 'PAY'}
                </Styled.DecisionPostButton>
              )}
            </Styled.DecisionAmountField>
          </Styled.DecisionInputGroup>
          <Styled.DecisionInputGroup>
            <Styled.DecisionInputLabel>Total Payout</Styled.DecisionInputLabel>
            <Styled.DecisionAmountField>
              <Styled.DecisionAmountPrefix>$</Styled.DecisionAmountPrefix>
              <Styled.DecisionAmountStatic>
                <CountUp value={totalPayout} format={formatPlainInt} />
              </Styled.DecisionAmountStatic>
            </Styled.DecisionAmountField>
          </Styled.DecisionInputGroup>
        </Styled.DecisionInputsRow>
        <Styled.DecisionFooterRow>
          <Styled.DecisionFooterLabel>Available Cash Balance:</Styled.DecisionFooterLabel>
          <Styled.DecisionFooterValue>
            <CountUp value={cashBalance} format={numberToM} />
          </Styled.DecisionFooterValue>
        </Styled.DecisionFooterRow>
      </Styled.DecisionPanel>
    );
  };

  renderInvestorCard = (investor, idx, opts) => {
    const { roundNumber } = this.props;
    const preset = pickPreset(investor.name, idx);
    const { withAnimation = true, hideSelectButton = false, showExisting = true } = opts || {};

    // Sign drives the verb (Invested vs Returned, Issued vs Returned).
    // The displayed numeric value is the absolute magnitude so it reads
    // naturally next to the label ("Capital Returned: $50,000", not "-$50,000").
    const capitalSign = Number(investor.capitalValueThisRound) || 0;
    const sharesSign = Number(investor.sharesValueThisRound) || 0;
    const capitalVerb = capitalSign >= 0 ? 'Invested' : 'Returned';
    const sharesVerb = sharesSign >= 0 ? 'Issued' : 'Returned';

    // Private Equity is a Year 3+ investor; Venture Capital is Year 2+.
    // Earlier rounds see the card dimmed with an "Available in Year N"
    // hover message and a disabled Select button — the picker stays
    // visible so students know the option exists but it can't be
    // committed until later.
    const isPrivateEquity = /private equity|p\.?e\.?$/i.test(investor.name || "")
      || preset.iconKey === "vault";
    const isVentureCapital = /venture/i.test(investor.name || "")
      || preset.iconKey === "rocket";
    const round = Number(roundNumber);
    let lockMessage = null;
    if (isPrivateEquity && round < 3) {
      lockMessage = "Available in Year 3";
    } else if (isVentureCapital && round < 2) {
      lockMessage = "Available in Year 2";
    }
    const isLocked = lockMessage !== null;

    const cardBody = (
      <Styled.EquityCard
        key={investor.client_id}
        shade={preset.shade}
        index={withAnimation ? idx : 0}
        noAnimation={!withAnimation}
      >
        <Styled.EquityCardTop>
          <Styled.EquityLogo>
            <InvestorArtFrame>{renderInvestorArt(preset.iconKey)}</InvestorArtFrame>
          </Styled.EquityLogo>
          <Styled.EquityTitleArea>
            <Styled.EquityName>{investor.name}</Styled.EquityName>
            {preset.tagline && <Styled.EquityTagline>{preset.tagline}</Styled.EquityTagline>}
            <Styled.EquityStageBadge>{preset.stage}</Styled.EquityStageBadge>
          </Styled.EquityTitleArea>
        </Styled.EquityCardTop>

        {preset.description && (
          <Styled.EquityDescription>{preset.description}</Styled.EquityDescription>
        )}

        <Styled.EquityInnerCard>
          <Styled.EquityInnerLabel>Max Amount</Styled.EquityInnerLabel>
          <Styled.EquityInnerDescription>
            Max amount investor can offer
          </Styled.EquityInnerDescription>
          <Styled.EquityInnerBigValue>
            <CountUp value={investor.maxAmount} format={formatFullDollar} />
          </Styled.EquityInnerBigValue>
        </Styled.EquityInnerCard>

        {showExisting && (
          <Styled.EquityInnerCard>
            <Styled.EquityInnerLabel>{`Capital ${capitalVerb} Year ${roundNumber}`}</Styled.EquityInnerLabel>
            <Styled.EquityExistingRow>
              <Styled.EquityExistingCol>
                <Styled.EquityExistingLabel>{`Capital ${capitalVerb}`}</Styled.EquityExistingLabel>
                <Styled.EquityExistingValue
                  style={capitalSign < 0 ? { color: '#FE7474' } : undefined}
                >
                  <CountUp value={capitalSign} format={formatFullDollar} />
                </Styled.EquityExistingValue>
              </Styled.EquityExistingCol>
              <Styled.EquityExistingCol>
                <Styled.EquityExistingLabel>{`Shares ${sharesVerb}`}</Styled.EquityExistingLabel>
                <Styled.EquityExistingValue
                  style={sharesSign < 0 ? { color: '#FE7474' } : undefined}
                >
                  <CountUp value={sharesSign} format={formatNumber} />
                </Styled.EquityExistingValue>
              </Styled.EquityExistingCol>
            </Styled.EquityExistingRow>
          </Styled.EquityInnerCard>
        )}

        {!hideSelectButton && (
          <Styled.EquitySelectButton onClick={() => this.handleSelectInvestorInternal(investor)}>
            Select Investor
          </Styled.EquitySelectButton>
        )}
      </Styled.EquityCard>
    );

    if (isLocked) {
      return (
        <LockedCardWrapper key={investor.client_id} title={lockMessage}>
          {cardBody}
          <LockedTooltip>{lockMessage}</LockedTooltip>
        </LockedCardWrapper>
      );
    }

    return cardBody;
  };

  render() {
    const { onClose } = this.props;
    const {
      loading,
      investors,
      companyValuation,
      netCapital,
      sharePrice,
      outstandingShares,
      selectedInvestorId,
      noticeOpen,
      noticeTitle,
      noticeMessage
    } = this.state;

    const selectedInvestor = selectedInvestorId
      ? investors.find(i => i.client_id === selectedInvestorId)
      : null;

    return (
      <Styled.LenderOverlay>
        <Styled.LenderModalHeader>
          <Styled.LenderModalTitle>Choose Your Equity Investor</Styled.LenderModalTitle>
          <Styled.LenderModalSubtitle>
            Select one or more equity investors to fund your business. Each comes with different terms.
          </Styled.LenderModalSubtitle>
          <Styled.LenderModalClose onClick={onClose} aria-label="Close">x</Styled.LenderModalClose>
        </Styled.LenderModalHeader>

        {loading && (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Loader type="ball-scale-ripple" active />
          </div>
        )}

        {!loading && (() => {
          // Show the "Capital Invested/Returned Year N" block whenever
          // any investor has round activity in either direction (issuance,
          // buyback, or dividend). A zero row is hidden so first-time
          // viewing of a fresh round stays clean.
          const anyExistingInvestment = investors.some(i =>
            Number(i.sharesValueThisRound) !== 0 || Number(i.capitalValueThisRound) !== 0
          );
          return (
          <div>
            <Styled.EquityStatusBar>
              {/* Net Capital Raised/Returned this round =
                  Σ equities.capital - (Σ repurchased_shares.cost +
                  Σ dividends_payments.payment). Positive = net inflow;
                  negative = net outflow (e.g. dividends paid). Red when
                  negative to mirror "cash used" elsewhere. */}
              <Styled.EquityStatusValuation>
                <Styled.EquityStatusLabel>Net Capital Raised/Returned</Styled.EquityStatusLabel>
                <Styled.EquityStatusValueBig style={{ color: netCapital < 0 ? '#C62828' : undefined }}>
                  <CountUp value={netCapital} format={numberToM} />
                </Styled.EquityStatusValueBig>
              </Styled.EquityStatusValuation>
              <Styled.EquityStatusValuation>
                <Styled.EquityStatusLabel>Company Valuation</Styled.EquityStatusLabel>
                <Styled.EquityStatusValueBig>
                  <CountUp value={companyValuation} format={numberToM} />
                </Styled.EquityStatusValueBig>
              </Styled.EquityStatusValuation>
              <Styled.EquityStatusSharePrice>
                <Styled.EquityStatusLabel>Share Price</Styled.EquityStatusLabel>
                <Styled.EquityStatusValueBig>
                  <CountUp value={sharePrice} format={formatSharePrice} />
                </Styled.EquityStatusValueBig>
              </Styled.EquityStatusSharePrice>
              <Styled.EquityStatusSharePrice last>
                <Styled.EquityStatusLabel>Outstanding Shares</Styled.EquityStatusLabel>
                <Styled.EquityStatusValueBig>
                  <CountUp value={outstandingShares} format={formatNumber} />
                </Styled.EquityStatusValueBig>
              </Styled.EquityStatusSharePrice>
            </Styled.EquityStatusBar>

            {!selectedInvestor && (
              <Styled.LenderGrid>
                {investors.map((investor, idx) =>
                  this.renderInvestorCard(investor, idx, { showExisting: anyExistingInvestment })
                )}
              </Styled.LenderGrid>
            )}

            {selectedInvestor && (
              <Styled.LenderSplit>
                <div>
                  <Styled.SelectedLenderWrapper>
                    {this.renderInvestorCard(selectedInvestor, 0, {
                      withAnimation: false,
                      hideSelectButton: true,
                      showExisting: anyExistingInvestment
                    })}
                  </Styled.SelectedLenderWrapper>
                  <Styled.ChooseDifferentLenderRow>
                    <Styled.ChooseDifferentLenderButton onClick={this.handleChooseDifferentInvestor}>
                      Choose a different Investor
                    </Styled.ChooseDifferentLenderButton>
                  </Styled.ChooseDifferentLenderRow>
                </div>
                {this.renderDecisionPanel(selectedInvestor)}
              </Styled.LenderSplit>
            )}
          </div>
          );
        })()}

        {noticeOpen && (() => {
          // Backend returns "No CEO Projections" when finance actions
          // are blocked by missing projections — show a button that
          // jumps the user to the CEO Decisions page instead of a
          // plain OK acknowledgement.
          const isNoCeoProjections =
            typeof noticeMessage === 'string'
            && /no ceo projections/i.test(noticeMessage);
          // Backend returns "Debt to capital ratio level too high" when
          // shrinking equity (issuing fewer shares / buying back) would
          // push the team's D/C ratio past a lender's cap. Replace the
          // raw string with a plain-language explanation of cause + fix.
          const isDebtCapitalBreach =
            typeof noticeMessage === 'string'
            && /debt to capital/i.test(noticeMessage);
          const handleAction = () => {
            this.hideNotice();
            if (isNoCeoProjections && typeof this.props.onGoToCeoProjections === 'function') {
              this.props.onGoToCeoProjections();
            }
          };
          let displayTitle = noticeTitle;
          let displayMessage = noticeMessage;
          if (isNoCeoProjections) {
            displayMessage = "Before you can raise capital, the CEO needs to submit financial projections for this round. Investors use those projections to decide how much they'll offer. Head to CEO Decisions to enter them, then come back to issue shares.";
          } else if (isDebtCapitalBreach) {
            displayTitle = "Unsuccessful! Debt to Capital Ratio Limit Breach";
            displayMessage = "Reducing your shares outstanding lowers your equity, which pushes your debt-to-capital ratio above your lender's limit. To make this adjustment, you'll have to reduce the amount of debt you hold if possible.";
          }
          return (
            <Styled.LenderNoticeOverlay onClick={this.hideNotice}>
              <Styled.LenderNoticeModal onClick={e => e.stopPropagation()}>
                <Styled.LenderNoticeTitle>{displayTitle}</Styled.LenderNoticeTitle>
                <Styled.LenderNoticeMessage>{displayMessage}</Styled.LenderNoticeMessage>
                <Styled.LenderNoticeActions>
                  <Styled.LenderNoticeButton onClick={handleAction}>
                    {isNoCeoProjections ? 'Check CEO Projections' : 'OK'}
                  </Styled.LenderNoticeButton>
                </Styled.LenderNoticeActions>
              </Styled.LenderNoticeModal>
            </Styled.LenderNoticeOverlay>
          );
        })()}
      </Styled.LenderOverlay>
    );
  }
}

export default EquityDecisionsPage;



// WEBPACK FOOTER //
// ./src/components/finance/decisions/equity-decisions-page.js